import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
  message,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { sanitizeReportHtml } from "../utils/sanitize";
import {
  api,
  NotifChannel,
  ReportConfig,
  ReportConfigBody,
  REPORT_SECTIONS,
} from "../api";

const { Text } = Typography;

const WINDOW_OPTIONS = [
  { value: "last24h",   label: "Last 24 hours" },
  { value: "yesterday", label: "Yesterday (00:00–23:59)" },
  { value: "custom",    label: "Custom time range" },
];

// knownSections drops section keys the report no longer has (a config saved
// before a section was retired still lists it). Empty means "all sections".
function knownSections(sections: string[]): string[] {
  const all: string[] = REPORT_SECTIONS.map(s => s.value);
  return sections.length ? sections.filter(s => all.includes(s)) : all;
}

function timeStrToDayjs(s: string): Dayjs | null {
  if (!s) return null;
  const [h, m] = s.split(":").map(Number);
  return dayjs().hour(h).minute(m).second(0);
}
function dayjsToTimeStr(d: Dayjs | null): string {
  if (!d) return "";
  return d.format("HH:mm:ss");
}

interface FormVals {
  name: string;
  enabled: boolean;
  send_at: Dayjs;
  window_type: string;
  window_start?: Dayjs;
  window_end?: Dayjs;
  sections: string[];
  channel_ids: number[];
  keep_history: number;
}

export default function Reports() {
  const [configs, setConfigs] = useState<ReportConfig[]>([]);
  const [channels, setChannels] = useState<NotifChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ReportConfig | null>(null);
  const [sending, setSending] = useState<number | null>(null);
  const [form] = Form.useForm<FormVals>();
  const windowType = Form.useWatch("window_type", form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgs, chs] = await Promise.all([api.reportConfigs(), api.notifChannels()]);
      setConfigs(cfgs);
      setChannels(chs);
    } catch (e: any) {
      message.error(e.message ?? "Load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      enabled: true,
      send_at: dayjs().hour(8).minute(0).second(0),
      window_type: "last24h",
      sections: REPORT_SECTIONS.map(s => s.value),
      channel_ids: [],
      keep_history: 30,
    });
    setModalOpen(true);
  }

  function openEdit(cfg: ReportConfig) {
    setEditing(cfg);
    form.resetFields();
    form.setFieldsValue({
      name: cfg.name,
      enabled: cfg.enabled,
      send_at: timeStrToDayjs(cfg.send_at) ?? dayjs().hour(8).minute(0),
      window_type: cfg.window_type,
      window_start: timeStrToDayjs(cfg.window_start) ?? undefined,
      window_end: timeStrToDayjs(cfg.window_end) ?? undefined,
      sections: knownSections(cfg.sections),
      channel_ids: cfg.channel_ids,
      keep_history: cfg.keep_history ?? 30,
    });
    setModalOpen(true);
  }

  async function handleSave() {
    const vals = await form.validateFields();
    const body: ReportConfigBody = {
      name: vals.name,
      enabled: vals.enabled ?? true,
      send_at: dayjsToTimeStr(vals.send_at),
      window_type: vals.window_type,
      window_start: vals.window_type === "custom" ? dayjsToTimeStr(vals.window_start ?? null) : "",
      window_end: vals.window_type === "custom" ? dayjsToTimeStr(vals.window_end ?? null) : "",
      sections: vals.sections ?? [],
      channel_ids: vals.channel_ids ?? [],
      keep_history: vals.keep_history ?? 30,
    };
    try {
      if (editing) {
        await api.updateReportConfig(editing.id, body);
        message.success("Report config updated");
      } else {
        await api.createReportConfig(body);
        message.success("Report config created");
      }
      setModalOpen(false);
      load();
    } catch (e: any) {
      message.error(e.message ?? "Save failed");
    }
  }

  async function handleDelete(id: number) {
    try {
      await api.deleteReportConfig(id);
      message.success("Deleted");
      load();
    } catch (e: any) {
      message.error(e.message ?? "Delete failed");
    }
  }

  async function handleSend(id: number) {
    setSending(id);
    const sentAt = new Date();
    try {
      await api.sendReport(id);
      message.loading({ content: "Generating report…", key: `send-${id}`, duration: 0 });
      // Poll history until the new entry appears (up to 3 min)
      for (let i = 0; i < 36; i++) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const hist = await api.reportHistory({ config_id: id, limit: 1 });
          if (hist.length && new Date(hist[0].sent_at) >= sentAt) {
            message.destroy(`send-${id}`);
            const entry = hist[0];
            if (entry.status === "sent") {
              message.success("Report sent");
            } else {
              message.error(`Report failed: ${entry.error || "unknown error"}`);
            }
            Modal.info({
              title: "Report Preview",
              width: 640,
              content: (
                <div style={{
                  background: "var(--bg-0)",
                  border: "1px solid var(--line)",
                  borderRadius: 6,
                  padding: "12px 16px",
                  whiteSpace: "pre-wrap",
                  fontSize: 13,
                  maxHeight: 500,
                  overflow: "auto",
                  lineHeight: 1.7,
                  fontFamily: "monospace",
                  color: "var(--text)",
                }} dangerouslySetInnerHTML={{ __html: sanitizeReportHtml(entry.summary || "(no content)") }} />
              ),
            });
            load();
            return;
          }
        } catch (_) { /* ignore poll errors */ }
      }
      // Timed out waiting — just reload
      message.destroy(`send-${id}`);
      message.info("Report is generating in background — check History");
      load();
    } catch (e: any) {
      message.error(e.message ?? "Send failed");
    } finally {
      setSending(null);
    }
  }

  const columns: ColumnsType<ReportConfig> = [
    {
      title: "Name",
      dataIndex: "name",
      render: (v, r) => (
        <Space>
          <Tag color={r.enabled ? "green" : "default"}>{r.enabled ? "ON" : "OFF"}</Tag>
          <Text strong style={{ whiteSpace: "nowrap" }}>{v}</Text>
        </Space>
      ),
    },
    {
      title: "Schedule",
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>Send at {r.send_at.slice(0, 5)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {WINDOW_OPTIONS.find(o => o.value === r.window_type)?.label ?? r.window_type}
          </Text>
        </Space>
      ),
    },
    {
      title: "Sections",
      dataIndex: "sections",
      render: (sections: string[]) => (
        <Flex wrap gap={4}>
          {knownSections(sections).map(s => (
            <Tag key={s}>{REPORT_SECTIONS.find(r => r.value === s)?.label}</Tag>
          ))}
        </Flex>
      ),
    },
    {
      title: "Channels",
      dataIndex: "channel_ids",
      render: (ids: number[]) => (
        <Flex wrap gap={4}>
          {ids.map(id => {
            const ch = channels.find(c => c.id === id);
            return <Tag key={id} color="blue">{ch?.name ?? `#${id}`}</Tag>;
          })}
        </Flex>
      ),
    },
    {
      title: "Actions",
      width: 160,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<SendOutlined />}
            loading={sending === r.id}
            onClick={() => handleSend(r.id)}>
            Send now
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Delete this report config?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Daily Reports</Typography.Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Report</Button>
        </Space>
      </Flex>

      <Table
        rowKey="id"
        dataSource={configs}
        columns={columns}
        scroll={{ x: "max-content" }}
        loading={loading}
        pagination={false}
        size="middle"
      />

      <Modal
        title={editing ? `Edit: ${editing.name}` : "New Report Config"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={620}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>Cancel</Button>,
          ...(editing ? [
            <Tooltip key="flush-tip" title="Save first, then send the report now to all channels">
              <Button
                key="flush"
                icon={<SendOutlined />}
                loading={sending === editing.id}
                onClick={async () => {
                  try { await form.validateFields(); } catch { return; }
                  await handleSave();
                  await handleSend(editing.id);
                }}
              >
                Send Now
              </Button>
            </Tooltip>
          ] : []),
          <Button key="save" type="primary" onClick={handleSave}>Save</Button>,
        ]}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="Daily summary" />
          </Form.Item>

          <Form.Item name="enabled" label="Enabled" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="send_at" label="Send at (server local time)" rules={[{ required: true }]}>
            <TimePicker format="HH:mm" minuteStep={15} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="window_type" label="Report window" rules={[{ required: true }]}>
            <Select options={WINDOW_OPTIONS} />
          </Form.Item>

          {windowType === "custom" && (
            <Flex gap={12}>
              <Form.Item name="window_start" label="From" style={{ flex: 1 }}>
                <TimePicker format="HH:mm" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="window_end" label="To" style={{ flex: 1 }}>
                <TimePicker format="HH:mm" style={{ width: "100%" }} />
              </Form.Item>
            </Flex>
          )}

          <Form.Item name="sections" label="Sections">
            <Checkbox.Group style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {REPORT_SECTIONS.map(s => (
                <Checkbox key={s.value} value={s.value}>{s.label}</Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>

          <Form.Item name="channel_ids" label="Send to channels">
            <Select
              mode="multiple"
              placeholder="Select Telegram / Webhook channels"
              options={channels.map(c => ({ value: c.id, label: `${c.name} (${c.type})` }))}
            />
          </Form.Item>

          <Form.Item
            name="keep_history"
            label="Keep history"
            extra="Automatically delete oldest records beyond this limit. 0 = keep all."
          >
            <InputNumber min={0} max={365} addonAfter="records" style={{ width: 180 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
